import { RefObject, useEffect } from 'react';

const SENDBIRD_MESSAGE_INPUT_SELECTORS = [
  '.sendbird-message-input--textarea[contenteditable="true"]',
  '.sendbird-message-input--textarea[contenteditable="true"]:not([contenteditable="false"])',
  '.sendbird-message-input--textarea',
];

const FOCUS_RETRY_DELAY_MS = 100;
const FOCUS_MAX_ATTEMPTS = 20;
const FOCUS_INITIAL_DELAY_MS = 50;

function findSendbirdMessageInput(root: ParentNode): HTMLElement | null {
  for (const selector of SENDBIRD_MESSAGE_INPUT_SELECTORS) {
    const input = root.querySelector<HTMLElement>(selector);
    if (!input || input.getAttribute('contenteditable') === 'false') {
      continue;
    }
    return input;
  }
  return null;
}

function placeCaretAtEnd(element: HTMLElement): void {
  const selection = window.getSelection();
  if (!selection) {
    return;
  }
  const range = document.createRange();
  range.selectNodeContents(element);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
}

/**
 * Focuses Sendbird UIKit message input (contentEditable div with placeholder "Enter message").
 */
export function focusSendbirdMessageInput(root: ParentNode = document): boolean {
  const input = findSendbirdMessageInput(root);
  if (!input) {
    return false;
  }

  input.focus({ preventScroll: false });
  placeCaretAtEnd(input);
  return document.activeElement === input;
}

/**
 * Retries focus until Sendbird UIKit mounts the message input after channel open.
 */
export function useFocusSendbirdMessageInput(
  isActive: boolean,
  channelUrl: string | undefined,
  rootRef?: RefObject<ParentNode | null>,
): void {
  useEffect(() => {
    if (!isActive || !channelUrl?.trim()) {
      return;
    }

    let cancelled = false;
    let attempts = 0;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const tryFocus = (): void => {
      if (cancelled) {
        return;
      }

      const root = rootRef?.current ?? document;
      const didFocus = focusSendbirdMessageInput(root);
      if (!didFocus && attempts < FOCUS_MAX_ATTEMPTS) {
        attempts += 1;
        timeoutId = setTimeout(tryFocus, FOCUS_RETRY_DELAY_MS);
      }
    };

    timeoutId = setTimeout(tryFocus, FOCUS_INITIAL_DELAY_MS);

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isActive, channelUrl, rootRef]);
}
